import { useTranslation } from "react-i18next";
import MoneyAmount from "@/shared/ui/components/money-amount";
import type { MoneyAmount as Money } from "@/shared/types/payment";

interface PlanAmountBreakdownProps {
  walletDiscount?: Money | null;
  exclusiveDiscount?: Money | null;
  dueToday: Money;
}

const hasValue = (m?: Money | null): m is Money =>
  !!m && typeof m.amount === "number" && m.amount > 0;

/** Discount amounts render green with a leading minus in LTR order (RTL-safe). */
function DiscountAmount({
  amount,
  currency,
  language,
}: {
  amount: number;
  currency: string;
  language: string;
}) {
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[14px] font-medium leading-6 tracking-[-0.21px] text-[#128351]"
      dir="ltr"
    >
      <span aria-hidden="true">-</span>
      <MoneyAmount
        amount={amount}
        currency={currency}
        language={language}
        className="inline-flex"
      />
    </span>
  );
}

export function PlanAmountBreakdown({
  walletDiscount,
  exclusiveDiscount,
  dueToday,
}: PlanAmountBreakdownProps) {
  const { t, i18n } = useTranslation();
  const showWallet = hasValue(walletDiscount);
  const showExclusive = hasValue(exclusiveDiscount);
  if (!showWallet && !showExclusive) return null;
  return (
    <div className="mt-3 space-y-2 border-t-[0.5px] border-[#DAE0EB] pt-3 text-[14px]">
      {showWallet && (
        <div className="flex justify-between" data-testid="breakdown-wallet-discount">
          <span className="text-gray-600">
            {t("single_page_checkout.breakdown.wallet_discount")}
          </span>
          <DiscountAmount
            amount={walletDiscount!.amount}
            currency={walletDiscount!.currency}
            language={i18n.language}
          />
        </div>
      )}
      {showExclusive && (
        <div className="flex justify-between" data-testid="breakdown-exclusive-discount">
          <span className="text-gray-600">
            {t("single_page_checkout.breakdown.exclusive_discount")}
          </span>
          <DiscountAmount
            amount={exclusiveDiscount!.amount}
            currency={exclusiveDiscount!.currency}
            language={i18n.language}
          />
        </div>
      )}
      <div className="flex justify-between font-semibold" data-testid="breakdown-due-today">
        <span className="text-gray-900">
          {t("single_page_checkout.breakdown.due_today")}
        </span>
        <MoneyAmount
          amount={dueToday.amount}
          currency={dueToday.currency}
          language={i18n.language}
          className="inline-flex"
        />
      </div>
    </div>
  );
}
